import { Button } from "@/components/ui/button";
import { Trash2, CheckCircle2, ChevronDown, Tag, Flag, Layers, CircleSlash, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import type { DropdownOption } from "@/types/backlog";
import HoldToConfirmButton from "@/components/shared/HoldToConfirmButton";

interface BulkActionBarProps {
  selectedCount: number;
  onMarkComplete: () => void;
  /** Fires the (recoverable) delete outright — the hold is the confirmation. */
  onDelete: () => void;
  statuses: DropdownOption[];
  onChangeStatus: (statusId: string) => void;
  priorities: DropdownOption[];
  onChangePriority: (priorityId: string) => void;
  onClearSelection: () => void;
  isAdmin: boolean;
}

const BulkActionBar = ({
  selectedCount,
  onMarkComplete,
  onDelete,
  statuses,
  onChangeStatus,
  priorities,
  onChangePriority,
  onClearSelection,
  isAdmin,
}: BulkActionBarProps) => {
  const [statusOpen, setStatusOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);

  return (
    <div className="flex items-center gap-2 px-6 py-3 border-b border-border">
      <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
      <Button size="sm" variant="ghost" onClick={onClearSelection} className="gap-1.5 text-muted-foreground">
        <X className="h-4 w-4" />
        Clear
      </Button>
      {isAdmin && (
        // Outlined green, the mirror of the outlined red at the other end of
        // this bar. Both are bulk verbs on the same selection, and a solid
        // fill on one of them made "Mark Complete" the loudest thing in the
        // toolbar — a standing invitation to close work in bulk. The pair now
        // reads as a pair: same weight, opposite colour, opposite meaning.
        //
        // 600 in light, 400 in dark: the done-status green family, one step
        // brighter against the dark card, which is what every other green in
        // the app does (see the focus screens' Done counts).
        <Button
          size="sm"
          variant="outline"
          onClick={onMarkComplete}
          className="gap-2 border-green-600/60 bg-transparent text-green-600 hover:border-green-600 hover:bg-green-600/5 hover:text-green-700 dark:border-green-400/60 dark:text-green-400 dark:hover:border-green-400 dark:hover:bg-green-400/10 dark:hover:text-green-300"
        >
          <CheckCircle2 className="h-4 w-4" />
          Mark Complete
        </Button>
      )}
      <Popover open={statusOpen} onOpenChange={setStatusOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="gap-2">
            <Tag className="h-4 w-4" />
            Change Status
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0 bg-card border-border z-[100]" align="start">
          <Command>
            <CommandList className="bg-card max-h-80">
              <CommandGroup>
                {statuses.map((status) => (
                  <CommandItem
                    key={status.id}
                    value={status.label}
                    onSelect={() => {
                      onChangeStatus(status.id);
                      setStatusOpen(false);
                    }}
                  >
                    <div className="h-3 w-3 rounded mr-2" style={{ backgroundColor: status.color }} />
                    <span>{status.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Popover open={priorityOpen} onOpenChange={setPriorityOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="gap-2">
            <Flag className="h-4 w-4" />
            Change Priority
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0 bg-card border-border z-[100]" align="start">
          <Command>
            <CommandList className="bg-card max-h-80">
              <CommandGroup>
                {priorities.map((priority) => (
                  <CommandItem
                    key={priority.id}
                    value={priority.label}
                    onSelect={() => {
                      onChangePriority(priority.id);
                      setPriorityOpen(false);
                    }}
                  >
                    <div className="h-3 w-3 rounded mr-2" style={{ backgroundColor: priority.color }} />
                    <span>{priority.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {isAdmin && (
        // Three times the single-row hold, because this is three times the
        // question: the same gesture, scaled to the blast radius.
        //
        // Outlined rather than filled: a solid destructive button sitting in
        // the bar the whole time a selection exists shouts louder than the
        // action deserves — and it had nowhere to *go* when held, since the
        // hold fill and the button were already the same red. Empty, it reads
        // as available; the fill sweeping across from the left is then the only
        // red on it, and the progress is unmistakable.
        <HoldToConfirmButton
          onConfirm={onDelete}
          holdMs={2000}
          fillFrom="left"
          title={`Delete ${selectedCount} item${selectedCount === 1 ? "" : "s"}`}
          className="inline-flex h-9 items-center justify-center rounded-md border border-destructive/60 bg-transparent px-3 text-sm font-medium text-destructive transition-colors hover:border-destructive hover:bg-destructive/5"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </HoldToConfirmButton>
      )}
    </div>
  );
};

export default BulkActionBar;
